import OpenButton from "@/components/toto/OpenButton";
import React, { useMemo, useState } from "react";
import { ArrowLeft, Check, ChevronLeft, ChevronRight, FileUp, Search, Star } from "lucide-react";
import { api } from "@/lib/api";
import { TotoAdaptiveDock } from "@/components/toto/TotoAdaptiveSurface";
import "./google-review-evidence.css";

function shortDate(value) {
  if (!value) return "Date unavailable";
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(new Date(`${value}T12:00:00`));
}

function hasSingleExactMatch(review) {
  return (review?.suggestions || []).filter((candidate) => Number(candidate.match_score) === 1).length === 1;
}

export default function GoogleReviewEvidence({ embedded = false }) {
  const [reviewFile, setReviewFile] = useState(null);
  const [salesFile, setSalesFile] = useState(null);
  const [session, setSession] = useState(null);
  const [index, setIndex] = useState(0);
  const [manualInvoice, setManualInvoice] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const automaticallyResolvedReviews = useMemo(
    () => (session?.reviews || []).filter((item) => !item.decision && hasSingleExactMatch(item)),
    [session]
  );
  const openReviews = useMemo(
    () => (session?.reviews || []).filter((item) => !item.decision && !hasSingleExactMatch(item)),
    [session]
  );
  const review = openReviews[index] || null;
  const decidedCount = useMemo(() => (session?.reviews || []).filter((item) => item.decision).length, [session]);
  const automaticallyResolvedCount = Math.max(session?.automatically_resolved_count || 0, automaticallyResolvedReviews.length);
  const resolvedCount = decidedCount + automaticallyResolvedReviews.length;

  async function prepare() {
    if (!reviewFile || !salesFile) {
      setError("Choose the Google Reviews export and the closed Sales Report.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const form = new FormData();
      form.append("review_file", reviewFile);
      form.append("sales_file", salesFile);
      const response = await api.post("/reviews/attribution/preview", form, { headers: { "Content-Type": "multipart/form-data" } });
      setSession(response.data);
      setIndex(0);
      setManualInvoice("");
      setNote("");
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "The evidence could not be prepared.");
    } finally {
      setLoading(false);
    }
  }

  async function decide(disposition, invoiceNumber = "") {
    if (!review || !session) return;
    setLoading(true);
    setError("");
    try {
      const response = await api.post(
        `/reviews/attribution/${session.session_id}/reviews/${encodeURIComponent(review.review_id)}/decision`,
        { disposition, invoice_number: invoiceNumber, note }
      );
      setSession(response.data);
      setManualInvoice("");
      setNote("");
      const remaining = response.data.reviews.filter((item) => !item.decision).length;
      setIndex((current) => Math.max(0, Math.min(current, remaining - 1)));
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "That decision could not be recorded.");
    } finally {
      setLoading(false);
    }
  }

  const content = (
    <>
      {!session ? (
        <section className="toto-review-evidence__plane toto-review-evidence__start" aria-labelledby="review-evidence-title">
          <h1 id="review-evidence-title">Review attribution</h1>
          <p className="toto-review-evidence__intro">Bring in the month’s Google Reviews export and its closed Sales Report. TOTO will propose likely guest-to-invoice matches; you make every final attribution.</p>
          <div className="toto-review-evidence__sources">
            <label>
              <span>Google Reviews export</span>
              <input type="file" accept=".csv,text/csv" onChange={(event) => setReviewFile(event.target.files?.[0] || null)} />
              <small className="toto-review-evidence__file-control"><FileUp size={14} aria-hidden="true" />{reviewFile ? reviewFile.name : "Choose export"}</small>
            </label>
            <label>
              <span>Closed Sales Report</span>
              <input type="file" accept=".csv,text/csv" onChange={(event) => setSalesFile(event.target.files?.[0] || null)} />
              <small className="toto-review-evidence__file-control"><FileUp size={14} aria-hidden="true" />{salesFile ? salesFile.name : "Choose report"}</small>
            </label>
          </div>
          {error && <p className="toto-review-evidence__error" role="alert">{error}</p>}
          <button className="toto-review-evidence__primary" type="button" onClick={prepare} disabled={loading}>
            <FileUp size={16} /> {loading ? "Preparing evidence…" : "Prepare review audit"}
          </button>
          <p className="toto-review-evidence__quiet-note">Only 5-star reviews are included. This step records evidence; it does not create a payout.</p>
        </section>
      ) : review ? (
        <section className="toto-review-evidence__plane toto-review-evidence__audit" aria-label="Review attribution audit">
          <header className="toto-review-evidence__audit-head">
            <div>
              <p className="toto-review-evidence__eyebrow">Needs review · {index + 1} of {openReviews.length}</p>
              <h1>One review at a time.</h1>
            </div>
            <p>{resolvedCount} resolved{automaticallyResolvedCount ? ` · ${automaticallyResolvedCount} exact` : ""}</p>
          </header>

          <div className="toto-review-evidence__review">
            <div className="toto-review-evidence__rating" aria-label="Five stars"><Star size={16} fill="currentColor" /> 5.0</div>
            <p className="toto-review-evidence__author">{review.reviewer_name || "Reviewer name unavailable"} <span>· {shortDate(review.review_date)}</span></p>
            <blockquote>{review.review_text || "This review has no written text. It can be recorded, but cannot establish a therapist mention."}</blockquote>
            {review.review_url && <OpenButton href={review.review_url} target="_blank" rel="noreferrer" aria-label="Open source review"/>}
          </div>

          <section className="toto-review-evidence__matches" aria-labelledby="match-heading">
            <div>
              <p className="toto-review-evidence__eyebrow" id="match-heading">Likely guest matches</p>
              <p>Suggestions are leads, not proof. Confirm a closed invoice to attribute its Experience Guide.</p>
            </div>
            {review.suggestions.length ? (
              <div className="toto-review-evidence__match-list">
                {review.suggestions.map((suggestion) => (
                  <button key={suggestion.invoice_number} type="button" onClick={() => decide("confirmed", suggestion.invoice_number)} disabled={loading}>
                    <span>{suggestion.guest_name}</span>
                    <small>{suggestion.sale_date} · Invoice {suggestion.invoice_number} · Closed by {suggestion.closed_by}</small>
                    <strong>{Math.round(suggestion.match_score * 100)}% lead <ChevronRight size={15} /></strong>
                  </button>
                ))}
              </div>
            ) : <p className="toto-review-evidence__empty">No reliable name match was found. Use the invoice number if you know it, or record why the review is not attributable.</p>}
          </section>

          <div className="toto-review-evidence__manual">
            <Search size={15} aria-hidden="true" />
            <input value={manualInvoice} onChange={(event) => setManualInvoice(event.target.value)} placeholder="Enter a known invoice number" aria-label="Known invoice number" />
            <button type="button" onClick={() => decide("confirmed", manualInvoice)} disabled={loading || !manualInvoice.trim()}>Use invoice</button>
          </div>

          <div className="toto-review-evidence__resolve">
            <input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Why is this review not attributable?" aria-label="Attribution note" />
            <button type="button" onClick={() => decide("unattributed")} disabled={loading || !note.trim()}>Record as unattributed</button>
          </div>
          {review.decision && <p className="toto-review-evidence__decision"><Check size={14} /> Recorded as {review.decision.disposition}{review.decision.invoice ? ` · invoice ${review.decision.invoice.invoice_number}` : ""}</p>}
          {error && <p className="toto-review-evidence__error" role="alert">{error}</p>}

          <footer className="toto-review-evidence__pager">
            <button type="button" onClick={() => setIndex(Math.max(0, index - 1))} disabled={index === 0 || loading}><ChevronLeft size={16} /> Previous</button>
            <button type="button" onClick={() => setIndex(Math.min(openReviews.length - 1, index + 1))} disabled={index === openReviews.length - 1 || loading}>Next <ChevronRight size={16} /></button>
          </footer>
        </section>
      ) : (
        <section className="toto-review-evidence__plane toto-review-evidence__complete" aria-labelledby="review-audit-complete-title">
          <h1 id="review-audit-complete-title">Nothing needs review.</h1>
          <p className="toto-review-evidence__intro">No remaining review needs your attention. Every five-star review is either decided or has one unambiguous exact name match.</p>
          {automaticallyResolvedCount > 0 && <p className="toto-review-evidence__quiet-note">{automaticallyResolvedCount} unambiguous exact match{automaticallyResolvedCount === 1 ? " was" : "es were"} recorded automatically. No review action is required.</p>}
          <button className="toto-review-evidence__primary" type="button" onClick={() => { setSession(null); setIndex(0); setReviewFile(null); setSalesFile(null); }}>Prepare another audit</button>
        </section>
      )}
    </>
  );

  return <main className={`toto-review-evidence${embedded ? " is-embedded" : ""}`} data-testid="google-review-evidence">{content}{!embedded && <TotoAdaptiveDock active="workspace" />}</main>;
}
